import { snakeToTitleCase } from 'src/lib/utils/formatLabel';

// ── Shared types ──────────────────────────────────────────────────────────────

export type TenantMember = {
  user_id: number;
  external_id: string;
  email: string;
  firstname?: string | null;
  surname?: string | null;
  job_title?: string | null;
  tenant_role: 'guest' | 'member' | 'champion' | 'admin';
  status: 'active' | 'invited' | 'pending_approval' | 'revoked' | 'deleted';
  status_updated_at?: string | null;
};

export type TenantMemberActor = {
  external_id: string;
  email: string;
  firstname?: string | null;
  surname?: string | null;
};

export type TenantMemberActivityEntry = {
  id: number;
  action: string;
  occurred_at: string;
  status: string;
  role: TenantMember['tenant_role'];
  invited_by?: TenantMemberActor | null;
};

export type TenantMemberDetails = {
  added_at: string;
  added_by?: TenantMemberActor | null;
  activity: TenantMemberActivityEntry[];
};

// ── Shared helpers ────────────────────────────────────────────────────────────

export const getUserDisplayName = (user: { firstname?: string | null; surname?: string | null; email: string }) => {
  const fullName = [user.firstname, user.surname].filter(Boolean).join(' ').trim();
  return fullName || user.email;
};

export const formatTenantRole = (role?: string | null) => (role ? snakeToTitleCase(role) : 'Member');

export const formatMembershipStatus = (status: TenantMember['status']) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'invited':
      return 'Invited';
    case 'pending_approval':
      return 'Pending approval';
    case 'revoked':
      return 'Revoked';
    case 'deleted':
      return 'Deleted';
    default:
      return status;
  }
};

export const formatMembershipActivityStatus = (status: string) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'requires_approval':
      return 'Pending approval';
    case 'revoked':
      return 'Revoked';
    default:
      return snakeToTitleCase(status);
  }
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

export const getMembershipStatusTooltip = (status: TenantMember['status']) => {
  switch (status) {
    case 'invited':
      return 'This person has been added to the tenant, but has not completed account setup yet.';
    case 'pending_approval':
      return 'This person has an account, but access to this tenant still requires approval.';
    case 'active':
      return 'This person has an active CompliMind account and can access this tenant.';
    case 'revoked':
      return 'This person no longer has access to this tenant.';
    case 'deleted':
      return 'This account has been removed from the platform.';
    default:
      return '';
  }
};

export const getMembershipStatusTone = (status: TenantMember['status']) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'invited':
      return 'bg-blue-100 text-blue-700';
    case 'pending_approval':
      return 'bg-amber-100 text-amber-700';
    case 'deleted':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-surface-hover text-content-subtle';
  }
};

export const describeMembershipActivity = (activity: TenantMemberActivityEntry) => {
  if (activity.action === 'ADD') return `Added to tenant as ${formatTenantRole(activity.role)}`;
  if (activity.action === 'DELETE') return 'Membership deleted';
  if (activity.status === 'revoked') return 'Membership revoked';
  if (activity.status === 'requires_approval') return 'Membership marked as pending approval';
  return 'Membership updated';
};
